import { useState } from 'react';

import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import { useDropdownPopover } from '@/shared-components/dropdown';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import Text from '@/shared-components/text/components/Text';

export default function SuartePlusSubscriptionCard({
  t,
  isFoundingGallery,
  showCancelButton,
  cancelDisabled,
  onOpenCancel,
  cancelButtonLabel,
  nextBillingLabel,
  amountLabel,
  isGallery,
  seatsSummary,
  seatCostLabel,
  galleryTotalLabel,
  showBusinessBillingAction,
  businessBillingLoading,
  onSwitchBusinessBilling,
  cancellationMessage,
  canRenew,
  renewing,
  onRenew,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { rootRef: menuRef, triggerRef: menuTriggerRef, menuId, close, toggle, handleTriggerKeyDown } = useDropdownPopover({
    open: isMenuOpen,
    onOpenChange: setIsMenuOpen,
    idPrefix: 'subscription-actions',
  });
  const hasBillingDetails = Boolean(
    isGallery && (amountLabel || seatsSummary || seatCostLabel || galleryTotalLabel)
  );
  const summaryAmountLabel = isGallery ? (galleryTotalLabel ?? amountLabel) : amountLabel;
  const hasMenuActions = showCancelButton || showBusinessBillingAction;

  const handleCancel = () => {
    close();
    onOpenCancel?.();
  };
  const handleBusinessBilling = () => {
    close();
    onSwitchBusinessBilling?.();
  };

  return (
    <div className="profile-subscriptions__card">
      <div className="profile-subscriptions__card-header">
        <div className="profile-subscriptions__card-identity">
          {isFoundingGallery && (
            <Text className="profile-subscriptions__card-founding">
              {t('labels.founding_gallery')}
            </Text>
          )}
          <div className="profile-subscriptions__card-top">
            <Text className="profile-subscriptions__card-plan">
              {isGallery ? 'Gallery Subscription' : 'Suarte Plus'}
            </Text>
          </div>
        </div>
        {hasMenuActions && (
          <div className="dropdown-menu dropdown-menu--wide profile-subscriptions__card-menu" ref={menuRef}>
            <button
              type="button"
              className="dropdown-menu__trigger"
              onClick={toggle}
              onKeyDown={handleTriggerKeyDown}
              aria-label="Subscription options"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              aria-controls={menuId}
              ref={menuTriggerRef}
            >
              <ThreeDotsHorizontal className="dropdown-menu__icon" />
            </button>
            <div id={menuId} className={`dropdown-menu__menu ${isMenuOpen ? 'is-open' : ''}`} role="menu">
              {showBusinessBillingAction && (
                <button
                  type="button"
                  className="dropdown-menu__item"
                  disabled={businessBillingLoading}
                  onClick={handleBusinessBilling}
                >
                  {t('actions.buy_as_business')}
                </button>
              )}
              {showCancelButton && (
                <button
                  type="button"
                  className="dropdown-menu__item dropdown-menu__item--destructive"
                  disabled={cancelDisabled}
                  onClick={handleCancel}
                >
                  {cancelButtonLabel}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="profile-subscriptions__card-summary" aria-label="Subscription billing summary">
        {summaryAmountLabel && (
          <div className="profile-subscriptions__card-summary-item">
            <Text className="profile-subscriptions__card-label">
              {isGallery ? t('labels.total') : t('labels.amount')}
            </Text>
            <Text className="profile-subscriptions__card-summary-value">{summaryAmountLabel}</Text>
          </div>
        )}
        <div className="profile-subscriptions__card-summary-item">
          <Text className="profile-subscriptions__card-label">{t('labels.next_billing')}</Text>
          <Text className="profile-subscriptions__card-summary-value">
            {isFoundingGallery ? '-' : nextBillingLabel}
          </Text>
        </div>
      </div>

      {hasBillingDetails && (
        <details className="profile-subscriptions__card-details">
          <summary>{t('actions.summary')}</summary>
          <div className="profile-subscriptions__card-grid">
            {amountLabel && (
              <div className="profile-subscriptions__card-detail">
                <Text className="profile-subscriptions__card-label">{t('labels.base_subscription')}</Text>
                <Text className="profile-subscriptions__card-value">{amountLabel}</Text>
              </div>
            )}
            {seatsSummary && (
              <div className="profile-subscriptions__card-detail">
                <Text className="profile-subscriptions__card-label">{t('labels.member_seats')}</Text>
                <Text className="profile-subscriptions__card-value">{seatsSummary}</Text>
              </div>
            )}
            {seatCostLabel && (
              <div className="profile-subscriptions__card-detail">
                <Text className="profile-subscriptions__card-label">{t('labels.seats_cost')}</Text>
                <Text className="profile-subscriptions__card-value">{seatCostLabel}</Text>
              </div>
            )}
            {galleryTotalLabel && (
              <div className="profile-subscriptions__card-detail">
                <Text className="profile-subscriptions__card-label">{t('labels.total')}</Text>
                <Text className="profile-subscriptions__card-value">{galleryTotalLabel}</Text>
              </div>
            )}
          </div>
        </details>
      )}

      {cancellationMessage && (
        <div className="profile-subscriptions__card-cancellation">
          <Text className="profile-subscriptions__card-cancellation-text">
            {cancellationMessage}
          </Text>
          {canRenew && (
            <ContinueButton
              loading={renewing}
              disabled={renewing}
              onClick={onRenew}
              className="profile-subscriptions__card-action-button"
            >
              {t('actions.renew_suarte_plus')}
            </ContinueButton>
          )}
        </div>
      )}
    </div>
  );
}
